import React, { useState, useEffect } from "react";
import { 
  Youtube, 
  Search, 
  Sparkles, 
  Maximize2, 
  ExternalLink, 
  Loader2, 
  Video, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  Tag, 
  BookOpen, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  ArrowUpDown
} from "lucide-react";

import { cn } from "../lib/utils";

export interface YouTubeVideo {
  id: string;
  lessonId: string;
  videoPath: string;
  title: string;
  description: string;
  tags: string[];
  status: string;
  youtubeVideoId: string;
  type: "landscape" | "short";
  partIndex?: number;
  totalParts?: number;
  timestamp: number;
  subject: string;
  topic: string;
}

export interface ChannelInfo {
  id: string;
  title: string;
  description: string;
  customUrl: string;
  thumbnailUrl: string;
  subscriberCount: string | null;
  videoCount: number;
  channelUrl: string;
  success: boolean;
}

export default function YouTubeHub() {
  const [channel, setChannel] = useState<ChannelInfo | null>(null);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [selectedType, setSelectedType] = useState<"all" | "landscape" | "short">("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title">("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    async function fetchData() {
      try {
        const [chanRes, vidRes] = await Promise.all([
          fetch("/api/youtube/channel-info"),
          fetch("/api/youtube/public-videos")
        ]);
        if (chanRes.ok) {
          const chanData = await chanRes.json();
          setChannel(chanData);
        }
        if (vidRes.ok) {
          const vidData = await vidRes.json();
          setVideos(vidData.videos || []);
        }
      } catch (err) {
        console.error("Failed to load YouTube hub data:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Filter and Search logic
  const filteredVideos = videos.filter(video => {
    const matchesSearch = 
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.topic.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSubject = selectedSubject === "all" || video.subject.toLowerCase() === selectedSubject.toLowerCase();
    const matchesType = selectedType === "all" || video.type === selectedType;

    return matchesSearch && matchesSubject && matchesType;
  });

  // Sort logic
  const sortedVideos = [...filteredVideos].sort((a, b) => {
    if (sortBy === "newest") return b.timestamp - a.timestamp;
    if (sortBy === "oldest") return a.timestamp - b.timestamp;
    if (sortBy === "title") return a.title.localeCompare(b.title);
    return 0;
  });

  // Unique subjects for filter tabs
  const subjects = ["all", ...Array.from(new Set(videos.map(v => v.subject.toLowerCase()).filter(Boolean)))];

  // Pagination logic
  const totalPages = Math.ceil(sortedVideos.length / itemsPerPage);
  const paginatedVideos = sortedVideos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Format Helper for numbers
  const formatNumber = (numStr: string | null) => {
    if (!numStr) return "N/A";
    const num = parseInt(numStr, 10);
    if (isNaN(num)) return numStr;
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section id="youtube-hub" className="bg-slate-950/40 border border-indigo-500/10 rounded-[2rem] p-8 sm:p-12 shadow-[0_0_50px_rgba(79,70,229,0.05)] relative overflow-hidden group hover:border-indigo-500/20 transition-all duration-500 max-w-5xl mx-auto w-full">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-red-500/5 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none translate-y-1/2 -translate-x-1/3"></div>

      <div className="relative z-10 space-y-10">
        {/* Hub Header & Channel Info */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-white/5">
          <div className="space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold tracking-wider uppercase">
              <Youtube className="w-3.5 h-3.5" /> Official Channel Hub
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-350">
              Professor X YouTube Hub
            </h2>
            <p className="text-slate-400 text-base max-w-2xl leading-relaxed">
              Explore dynamic, animated lecture video solutions generated automatically on our platform and published directly to our central catalog of cinematic tutorials.
            </p>
            <div className="pt-2">
              <a 
                href="https://www.youtube.com/@ProfessorXAI-108"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-5 py-2.5 border border-red-500/20 hover:border-red-500/70 bg-red-500/5 hover:bg-red-600 hover:text-white text-rose-400 rounded-2xl text-xs font-black tracking-wider transition-all cursor-pointer shadow-sm hover:shadow-[0_0_20px_rgba(239,68,68,0.35)]"
                title="Visit Official Channel @ProfessorXAI-108"
              >
                <Youtube className="w-4.5 h-4.5 text-red-500 fill-red-500 group-hover:text-white group-hover:fill-white transition-colors duration-300" />
                <span className="uppercase font-sans font-extrabold tracking-widest">
                  @ProfessorXAI-108
                </span>
              </a>
            </div>
          </div>

          {channel && (
            <div className="flex items-center gap-4 bg-white/5 border border-white/5 rounded-2xl p-4 md:p-5 hover:bg-white/10 transition-colors duration-300">
              <img 
                src={channel.thumbnailUrl || "/profxlogo.png"} 
                alt={channel.title} 
                className="w-14 h-14 rounded-full border border-red-500/30 object-cover bg-black"
                referrerPolicy="no-referrer"
              />
              <div className="space-y-1">
                <h4 className="font-extrabold text-white text-base flex items-center gap-1">
                  {channel.title}
                  <CheckCircle2 className="w-4 h-4 text-blue-400 fill-blue-400" />
                </h4>
                <p className="text-indigo-300 text-xs font-bold">{channel.customUrl}</p>
                
                <div className="flex gap-4 text-xs text-slate-400 pt-1">
                  <div>
                    <span className="font-bold text-slate-200">{formatNumber(channel.subscriberCount)}</span> Subs
                  </div>
                  <div>
                    <span className="font-bold text-slate-200">{channel.videoCount}</span> Videos
                  </div>
                </div>
              </div>
              <a 
                href={channel.channelUrl || "https://www.youtube.com/@ProfessorXAI-108"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-4 p-3 rounded-full bg-red-600 hover:bg-red-500 text-white self-center transition-colors shadow-[0_0_15px_rgba(239,68,68,0.4)]"
                title="Visit Channel"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>



        {/* Video Library Search, Categorization, & Filters */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search videos by title, topic, subject..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white/5 hover:bg-white/10 focus:bg-slate-900 border border-white/5 focus:border-indigo-500/50 rounded-2xl pl-11 pr-4 py-3 text-slate-200 placeholder-slate-400 text-sm font-medium focus:ring-0 outline-none transition-all"
              />
            </div>

            {/* Filter controls */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Type Filter */}
              <div className="flex bg-white/5 rounded-xl border border-white/5 p-1 text-xs">
                <button 
                  onClick={() => { setSelectedType("all"); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-lg font-bold transition-all ${selectedType === "all" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
                >
                  All Formats
                </button>
                <button 
                  onClick={() => { setSelectedType("landscape"); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-lg font-bold transition-all ${selectedType === "landscape" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
                >
                  Landscape
                </button>
                <button 
                  onClick={() => { setSelectedType("short"); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-lg font-bold transition-all ${selectedType === "short" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
                >
                  Shorts
                </button>
              </div>

              {/* Sorting Selector */}
              <div className="flex items-center gap-2 bg-white/5 border border-white/5 rounded-xl px-3 py-1.5 text-xs text-slate-300">
                <ArrowUpDown className="w-3.5 h-3.5 text-indigo-400" />
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="bg-transparent text-slate-200 outline-none border-none pr-4 font-bold cursor-pointer"
                >
                  <option value="newest" className="bg-slate-900 text-slate-200 font-bold">Newest First</option>
                  <option value="oldest" className="bg-slate-900 text-slate-200 font-bold">Oldest First</option>
                  <option value="title" className="bg-slate-900 text-slate-200 font-bold">Title (A-Z)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Subject Filter Tabs */}
          {subjects.length > 2 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
              <Filter className="w-4 h-4 text-indigo-400 flex-shrink-0" />
              <div className="flex gap-2">
                {subjects.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => { setSelectedSubject(sub); setCurrentPage(1); }}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border capitalize ${
                      selectedSubject === sub 
                        ? "bg-indigo-600 border-indigo-500 text-white shadow-[0_0_10px_rgba(79,70,229,0.4)]" 
                        : "bg-white/5 border-white/5 text-slate-400 hover:border-white/10 hover:text-white"
                    }`}
                  >
                    {sub === "all" ? "All Subjects" : sub}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Video Grid Feed */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              <p className="text-sm font-semibold">Synchronizing with Professor X catalog...</p>
            </div>
          ) : paginatedVideos.length === 0 ? (
            <div className="bg-white/5 border border-white/5 rounded-2xl py-16 text-center text-slate-400">
              <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-base font-bold">No uploaded videos match the filters.</p>
              <p className="text-xs text-slate-500 mt-1">Start by generating an AI lesson and sending it to YouTube!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              {paginatedVideos.map((video) => (
                <div 
                  key={video.id}
                  className="bg-slate-900/40 border border-white/5 hover:border-indigo-500/20 rounded-2xl overflow-hidden group/card hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
                >
                  {/* Thumbnail Cover Layer */}
                  <div className="aspect-video relative bg-black/85 flex items-center justify-center overflow-hidden border-b border-white/5">
                    <img 
                      src={`https://img.youtube.com/vi/${video.youtubeVideoId}/mqdefault.jpg`} 
                      alt={video.title} 
                      className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Format Badge */}
                    <span className={`absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${video.type === "short" ? "bg-red-600 text-white" : "bg-indigo-600 text-white"}`}>
                      {video.type === "short" ? "Short" : "Landscape"}
                    </span>

                    {/* Overlay play button action */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/card:opacity-100 flex items-center justify-center transition-opacity duration-300">
                      <a 
                        href={`https://youtube.com/watch?v=${video.youtubeVideoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-red-600 rounded-full text-white shadow-lg shadow-red-600/50 scale-90 group-hover/card:scale-100 transition-all duration-300"
                      >
                        <ExternalLink className="w-5 h-5 animate-pulse" />
                      </a>
                    </div>
                  </div>

                  {/* Metadata and text details */}
                  <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold capitalize">
                        <span className="text-indigo-400 font-extrabold flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> {video.subject}
                        </span>
                        <span>•</span>
                        <span className="text-purple-400 flex items-center gap-1">
                          <Tag className="w-3 h-3" /> {video.topic}
                        </span>
                      </div>

                      <h4 className="font-extrabold text-slate-100 group-hover/card:text-indigo-300 transition-colors line-clamp-2 leading-snug text-sm">
                        {video.title}
                      </h4>
                      <p className="text-slate-400 text-xs line-clamp-2 leading-relaxed">
                        {video.description}
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-500 pt-3 border-t border-white/5 mt-auto">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(video.timestamp)}
                      </span>
                      <a 
                        href={`https://youtube.com/watch?v=${video.youtubeVideoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-500 hover:text-red-400 font-bold flex items-center gap-1 transition-colors"
                      >
                        Watch on YT
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-6 border-t border-white/5">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 border border-white/5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 disabled:opacity-40 disabled:hover:bg-white/5 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-xs text-slate-400 font-bold">
                Page <span className="text-slate-200">{currentPage}</span> of {totalPages}
              </div>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 border border-white/5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 disabled:opacity-40 disabled:hover:bg-white/5 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
